import settings, { ineligibleStatuses } from '@/data/constants';
import type { ConsultationStatusMapped } from '@/hooks/rest/useGetConsultationStatus';
import type { ConsultationSubStatusType } from '@/types';
import { Button } from '@montugroup/design-system';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { Menu, MenuItem, Stack } from '@mui/material';
import { useMemo, useState } from 'react';

export interface ConsultationStatusSelectorProps {
  statusId?: number;
  subStatusId?: number;
  disabled?: boolean;
  consultationStatuses: ConsultationStatusMapped[];
  handleStatusChange?: (statusId: number) => void;
  handleSubStatusChange?: (status: ConsultationSubStatusType) => void;
}

export const ConsultationStatusSelector = ({
  statusId,
  subStatusId,
  disabled,
  consultationStatuses,
  handleStatusChange,
  handleSubStatusChange,
}: ConsultationStatusSelectorProps) => {
  const [consultationStatusAnchorEl, setConsultationStatusAnchorEl] = useState<null | HTMLElement>(null);
  const [inEligibilityReasonAnchorEl, setInEligibilityReasonAnchorEl] = useState<null | HTMLElement>(null);

  const handleClose = (): void => {
    setConsultationStatusAnchorEl(null);
    setInEligibilityReasonAnchorEl(null);
  };

  const handleConsultationStatusChange = (selectedStatus: number) => {
    handleStatusChange && handleStatusChange(selectedStatus);
    handleClose();
  };

  const handleIneligibilityStatusChange = (selectedSubStatus: number) => {
    const statusObj = ineligibleStatuses.find((status) => status.value === selectedSubStatus);
    if (handleSubStatusChange && statusObj) {
      handleSubStatusChange({ name: statusObj.name, id: statusObj.value });
    }
    handleClose();
  };

  const selectedStatusName = useMemo(() => {
    const status = consultationStatuses?.find((status) => status.value === statusId);
    return status ? status.name : 'Consultation Status';
  }, [consultationStatuses, statusId]);

  const selectedIneligibilityReason = useMemo(() => {
    const status = ineligibleStatuses.find((status) => status.value === subStatusId);
    return status ? status.name : 'Ineligibility Reason';
  }, [subStatusId]);

  const renderSelection = () => (
    <Stack direction="row" alignItems="center">
      <>
        <Button
          variant="outlined"
          onClick={(e) => setConsultationStatusAnchorEl(e.currentTarget)}
          endIcon={<ArrowDropDownIcon fontSize="small" />}
          disabled={disabled}
        >
          {selectedStatusName}
        </Button>
        <Menu
          anchorEl={consultationStatusAnchorEl}
          open={Boolean(consultationStatusAnchorEl)}
          onClose={() => setConsultationStatusAnchorEl(null)}
          id="consultation-status"
        >
          {consultationStatuses?.map((status) => (
            <MenuItem
              key={status.value}
              onClick={() => handleConsultationStatusChange(status.value)}
              value={status.value}
            >
              {status.name}
            </MenuItem>
          ))}
        </Menu>
      </>

      {statusId === settings.consultationStatus.ineligible && (
        <>
          <Button
            variant="outlined"
            onClick={(e) => setInEligibilityReasonAnchorEl(e.currentTarget)}
            endIcon={<ArrowDropDownIcon fontSize="small" />}
          >
            {selectedIneligibilityReason}
          </Button>
          <Menu
            anchorEl={inEligibilityReasonAnchorEl}
            open={Boolean(inEligibilityReasonAnchorEl)}
            onClose={() => setInEligibilityReasonAnchorEl(null)}
            id="consultation-sub-status"
          >
            {ineligibleStatuses.map((status) => (
              <MenuItem
                key={status.value}
                onClick={() => handleIneligibilityStatusChange(status.value)}
                value={status.value}
              >
                {status.name}
              </MenuItem>
            ))}
          </Menu>
        </>
      )}
    </Stack>
  );

  return renderSelection();
};
